import type { KpiItem } from "../../../types";
import SectionCard from "./SectionCard";

interface KpiSectionProps {
  kpis: KpiItem[];
  t: (key: string, opts?: Record<string, unknown>) => string;
  onKpiClick: (kpiId: number) => void;
}

export default function KpiSection({ kpis, t, onKpiClick }: KpiSectionProps) {
  return (
    <SectionCard title={t("detail.kpiSection")}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {kpis.map((kpi) => {
          const total = kpi.subtask_count ?? kpi.first_level_tasks_total;
          const done = kpi.subtasks_done ?? kpi.first_level_tasks_done;
          const pct = total > 0 ? (done / total) * 100 : 0;

          function handleClick() {
            onKpiClick(kpi.id);
          }

          function handleKeyDown(e: React.KeyboardEvent) {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onKpiClick(kpi.id);
            }
          }

          return (
            <div
              key={kpi.id}
              role="button"
              tabIndex={0}
              onClick={handleClick}
              onKeyDown={handleKeyDown}
              className="flex flex-col gap-3 p-4 radius-md border surface-component-stroke cursor-pointer hover:surface-component-fill transition-colors"
            >
              <div className="flex justify-between items-center gap-2">
                <p className="text-body-bold-md content-base-primary">{kpi.title}</p>

                <p className="text-body-regular-md content-base-low">
                  <span className="text-body-bold-md content-base-primary">{done}</span> / {total}
                </p>
              </div>

              <div className="h-1.5 w-full rounded-full surface-component-fill overflow-hidden">
                <div className="h-full rounded-full background-brand-fill" style={{ width: `${pct}%` }} />
              </div>

              <p className="text-body-regular-sm content-base-secondary">
                {pct === 0 ? (
                  t("detail.kpiTasksCompleted", { percent: 0 })
                ) : (
                  <>
                    {t("detail.kpiTasksCompletedPrefix")}
                    <span className="text-body-bold-sm content-action-brand">{Math.round(pct)}%</span>
                    {t("detail.kpiTasksCompletedSuffix")}
                  </>
                )}
              </p>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}
